/**
 * Utilitaires partagés autour des erreurs tRPC côté client ERP.
 *
 * Centralise les patterns récurrents (extraction du code d'erreur, mapping
 * code → message FR) pour éviter la duplication entre Login / Signup /
 * ResetPassword / MagicLink.
 */

/**
 * Extrait le code d'erreur tRPC d'une exception ("UNAUTHORIZED", "BAD_REQUEST",
 * "CONFLICT", "FORBIDDEN", "TOO_MANY_REQUESTS", etc.).
 *
 * Volontairement en duck-typing (pas `instanceof TRPCClientError`) : dans un
 * monorepo pnpm, plusieurs instances de la classe peuvent coexister selon le
 * hoisting des dépendances → le check `instanceof` échoue silencieusement et
 * on tomberait toujours dans le fallback "erreur réseau". On lit donc
 * directement les champs attendus du payload.
 *
 * Le rate limiter Hono intervient avant le handler tRPC et renvoie donc un
 * HTTP 429 sans enveloppe tRPC. Le client conserve néanmoins le statut dans
 * `meta.response.status` : on le traduit ici pour que l'interface affiche la
 * vraie cause au lieu d'une fausse « connexion au serveur impossible ».
 *
 * Retourne `null` pour toute autre erreur qui n'expose ni code tRPC ni statut
 * HTTP reconnu (réseau coupé, CORS, parsing, exception non-tRPC).
 */
export function extractTrpcCode(error: unknown): string | null {
  if (!error || typeof error !== "object") return null;
  const candidate = error as {
    data?: { code?: unknown } | null;
    shape?: { data?: { code?: unknown } | null } | null;
    meta?: { response?: { status?: unknown } | null } | null;
  };
  const direct = candidate.data?.code;
  if (typeof direct === "string") return direct;
  const fromShape = candidate.shape?.data?.code;
  if (typeof fromShape === "string") return fromShape;
  if (candidate.meta?.response?.status === 429) return "TOO_MANY_REQUESTS";
  return null;
}

/**
 * Extrait le champ `data.cause` d'une erreur tRPC (Lot B.2.6 — exposé par
 * `errorFormatter` côté serveur). Permet de distinguer des cas spécifiques
 * partageant le même code HTTP, ex: `MFA_REQUIRED_MULTI_TENANT` parmi les
 * `FORBIDDEN`.
 */
export function extractTrpcCause(error: unknown): string | null {
  if (!error || typeof error !== "object") return null;
  const candidate = error as {
    data?: { cause?: unknown } | null;
    shape?: { data?: { cause?: unknown } | null } | null;
  };
  const direct = candidate.data?.cause;
  if (typeof direct === "string") return direct;
  const fromShape = candidate.shape?.data?.cause;
  if (typeof fromShape === "string") return fromShape;
  return null;
}

/**
 * Message générique à afficher quand l'erreur n'est pas typée tRPC :
 * réseau coupé, backend down, CORS bloqué, etc. Volontairement neutre.
 */
export const NETWORK_ERROR_MESSAGE = "Connexion au serveur impossible. Veuillez réessayer.";
